import React, { useState } from 'react';
import { X, ExternalLink, FileText, Image as ImageIcon, Loader, Download } from 'lucide-react';

interface FilePreviewProps {
  url: string;
  name?: string;
  className?: string;
}

const FilePreview: React.FC<FilePreviewProps> = ({ url, name, className }) => {
  const [previewOpen, setPreviewOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  
  const isImage = (url: string): boolean => {
    return /\.(jpg|jpeg|png|gif|webp|svg)(\?.*)?$/i.test(url);
  };
  
  const isPDF = (url: string): boolean => {
    return /\.(pdf)(\?.*)?$/i.test(url);
  };

  const openPreview = () => {
    setPreviewOpen(true);
    setIsLoading(true);
  };

  const closePreview = () => {
    setPreviewOpen(false);
  };
  
  const handleImageLoad = () => {
    setIsLoading(false);
  };
  
  const handleImageError = () => {
    setIsLoading(false);
  };
  
  const handleIframeLoad = () => {
    setIsLoading(false);
  };
  
  const getFileName = (): string => {
    if (name) return name;
    
    try {
      // Extract filename from Firebase URL
      if (url.includes('firebasestorage.googleapis.com')) {
        const filePathMatch = url.match(/o\/([^?]+)/);
        if (filePathMatch && filePathMatch[1]) {
          let filePath = decodeURIComponent(filePathMatch[1]);
          const parts = filePath.split('/');
          return parts[parts.length - 1];
        }
      }
      
      const urlParts = url.split('/');
      let fileName = urlParts[urlParts.length - 1];
      fileName = fileName.split('?')[0];
      return fileName;
    } catch (error) {
      return "File";
    }
  };

  const getFileIcon = () => {
    if (isImage(url)) {
      return <ImageIcon className="w-5 h-5 text-blue-500" />;
    } else if (isPDF(url)) {
      return <FileText className="w-5 h-5 text-red-500" />;
    } else {
      return <FileText className="w-5 h-5 text-gray-500" />;
    }
  };

  const getFileType = (): string => {
    if (isImage(url)) return 'Image';
    if (isPDF(url)) return 'PDF';
    return 'Document';
  };

  return (
    <>
      <div 
        className={`group relative overflow-hidden rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 shadow-sm hover:shadow-md transition-all duration-300 ${className || ''}`}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onClick={openPreview}
      >
        {isImage(url) && (
          <div className="aspect-video w-full overflow-hidden">
            <img 
              src={url} 
              alt={getFileName()}
              className="w-full h-full object-cover transform group-hover:scale-105 transition-transform duration-300"
            />
          </div>
        )}
        
        <div className="p-3">
          <div className="flex items-start gap-3">
            <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50">
              {getFileIcon()}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-medium text-gray-900 dark:text-white truncate">
                {getFileName()}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {getFileType()}
              </p>
            </div>
          </div>
        </div>

        <div className={`absolute inset-0 bg-black/60 flex items-center justify-center gap-2 transition-opacity duration-300 ${isHovered ? 'opacity-100' : 'opacity-0'}`}>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              window.open(url, '_blank');
            }}
            className="p-2 rounded-full bg-white/90 hover:bg-white text-gray-900 transition-colors"
            title="Download"
          >
            <Download className="w-5 h-5" />
          </button>
          <button 
            onClick={(e) => {
              e.stopPropagation();
              openPreview();
            }}
            className="p-2 rounded-full bg-white/90 hover:bg-white text-gray-900 transition-colors"
            title="Preview"
          >
            <ExternalLink className="w-5 h-5" />
          </button>
        </div>
      </div>

      {previewOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="flex justify-between items-center p-4 border-b dark:border-gray-700">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-gray-50 dark:bg-gray-700/50">
                  {getFileIcon()}
                </div>
                <div>
                  <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                    {getFileName()}
                  </h3>
                  <p className="text-sm text-gray-500 dark:text-gray-400">
                    {getFileType()}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <a 
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                  title="Download"
                >
                  <Download className="w-5 h-5" />
                </a>
                <button 
                  onClick={closePreview}
                  className="p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700 transition-colors"
                >
                  <X className="w-5 h-5" />
                </button>
              </div>
            </div>
            
            <div className="p-4 overflow-auto max-h-[calc(90vh-8rem)] relative">
              {isLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-white/80 dark:bg-gray-800/80 backdrop-blur-sm z-10">
                  <div className="flex flex-col items-center">
                    <Loader className="w-8 h-8 text-primary-500 animate-spin" />
                    <p className="mt-2 text-sm text-gray-600 dark:text-gray-400">Loading file...</p>
                  </div>
                </div>
              )}
              
              {isImage(url) ? (
                <div className="flex items-center justify-center">
                  <img 
                    src={url} 
                    alt={getFileName()} 
                    className="max-w-full max-h-[70vh] object-contain rounded-lg shadow-lg"
                    onLoad={handleImageLoad}
                    onError={handleImageError}
                  />
                </div>
              ) : isPDF(url) ? (
                <div className="aspect-[3/4] w-full">
                  <iframe 
                    src={`${url}#view=fit`} 
                    className="w-full h-full border-0 rounded-lg shadow-lg"
                    title={getFileName()}
                    onLoad={handleIframeLoad}
                  ></iframe>
                </div>
              ) : (
                <div className="text-center py-12">
                  <FileText className="w-16 h-16 mx-auto text-gray-400" />
                  <p className="mt-4 text-gray-600 dark:text-gray-400">
                    This file type cannot be previewed.
                  </p>
                  <a 
                    href={url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
                  >
                    <Download className="w-4 h-4" />
                    Download File
                  </a>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default FilePreview; 